// Foundations
import {
  Component,
  ChangeDetectorRef,
  EventEmitter,
  OnInit,
  Output,
  Input,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DateRange } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup } from '@angular/forms';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { finalize, first, delay, debounceTime } from 'rxjs/operators';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';
import { DialogService } from '../../../services/dialog.service';
import { PaymentService } from 'src/app/services/payment.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { MyProfileService } from 'src/app/services/my-profile.service';

// Components
import { AppComponent } from 'src/app/app.component';
import { CanDeactivateComponent } from '../../../models/can-deactivate-component.model';
import { ReservationVaccineDialogComponent } from './reservation-vaccine-dialog/reservation-vaccine-dialog.component';
import { PaymentDetailsFormComponent } from '../../_share/payment-details-form/payment-details-form.component';

// Models
import { ReservationRequestItem } from 'src/app/models/reservation/reservation-request.model';
import { CategoriesWithRules } from 'src/app/models/reservation/reservation-types';
import { ReservationPetInfo } from 'src/app/models/reservation-pet-info.model';
import { ReservationServiceItem } from 'src/app/models/reservation/reservation-service.model';
import { PaymentCreditCard } from 'src/app/models/payment/payment-card.model';
import { FranchiseLocation } from 'src/app/models/franchise.model';

import moment from 'moment';
import * as lodash from 'lodash';

@Component({
  selector: 'app-reservation-summary',
  templateUrl: './reservation-summary.component.html',
  styleUrls: ['./reservation-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationSummaryComponent
  implements OnInit, OnDestroy, CanDeactivateComponent
{
  // #region -> Property <-
  // ----------

  @Input() hasBoardingServiceSelected = false;
  @Input() reservationRequest$: Observable<ReservationRequestItem>;
  reservationRequest: ReservationRequestItem = new ReservationRequestItem({});
  reservationRequestSubscription: Subscription;

  @Output() navigateToNext = new EventEmitter();

  @ViewChild('paymentDetailForm', { static: false })
  paymentDetailForm: PaymentDetailsFormComponent;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - UI <-
  // ----------

  selectedLocation: FranchiseLocation;

  petList: ReservationPetInfo[];
  petListWithExpiredVaccinations: ReservationPetInfo[];
  petNames: { [petId: string]: string } = {};

  coreServices: { [categoryName: string]: ReservationServiceItem[] };

  dateRange: DateRange<any>;
  startDate: string;
  endDate: string;

  addonServices: { [petId: string]: ReservationServiceItem[] } = {};

  reservationNotes: string;

  total: number;

  // I understand this policy on boarding reservations
  checkboxConsent = false;

  // If the "selected location" has enabled CCP (Credit Card Processing).
  isCcpEnabledInLocation = false;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Payment Method <-
  // ----------

  paymentCreditCardsSubscription: Subscription;
  hasPaymentMethod;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Modal <-
  // ----------

  hasExpiredVaccination = false;
  modalOpened = false;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private appComponent: AppComponent,
    private dialogService: DialogService,
    private route: ActivatedRoute,
    private router: Router,
    private _analytics: AnalyticsService,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: NgbModal,
    private dialog: MatDialog,
    private loadingIndicatorService: LoadingIndicatorService,
    private paymentService: PaymentService,
    private myProfileService: MyProfileService
  ) {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    this.paymentService.getPaymentCreditCards().subscribe();

    //
    this.reservationRequestSubscription = this.reservationRequest$.subscribe(
      (newReservationRequest) => {
        this.reservationRequest = this.reservationRequest.update({
          ...newReservationRequest.json(),
        });

        //
        this._resetData();

        // Trigger change detection when mutated.
        this.changeDetectorRef.markForCheck();
      }
    );

    //
    this.paymentCreditCardsSubscription =
      this.paymentService.paymentCreditCards$.subscribe(
        (paymentCreditCards) => {
          //
          this.hasPaymentMethod = this.paymentService.hasValidPaymentCard();

          //
          this.changeDetectorRef.markForCheck();
        }
      );
  }

  ngOnDestroy(): void {
    //
    this.reservationRequestSubscription.unsubscribe();
  }

  canDeactivate() {
    //
    if (this.hasExpiredVaccination) {
      // Have expired vaccinations
      this.modalService.open(
        {},
        {
          backdrop: 'static',
        }
      );
      return false;
    }

    //
    return true;
  }

  reloadUIElements() {
    // Reload the "Payment iFrame" if needed.
    if (
      this.reservationRequest.includeCoreCategory(CategoriesWithRules.Boarding)
    ) {
      this.hasBoardingServiceSelected = true;
      // Only call "load" func if the component is there
      if (!this.hasPaymentMethod && this.paymentDetailForm) {
        this.paymentDetailForm.loadPaymentIframe();
      }
    } else {
      this.hasBoardingServiceSelected = false;
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI <-
  // ----------
  get paymentFormValid(): boolean {
    if (
      this.isCcpEnabledInLocation &&
      this.hasBoardingServiceSelected &&
      !this.hasPaymentMethod
    ) {
      return this.paymentDetailForm?.form?.valid;
    }
    return true;
  }

  callCamp(): void {
    this._analytics.logButtonTap('requestInterview_callCampLocation');
  }

  submitReservation(): void {
    /*
      Start with "payment form" if needed
    */
    if (
      this.isCcpEnabledInLocation &&
      this.hasBoardingServiceSelected &&
      !this.hasPaymentMethod
    ) {
      //
      this.paymentDetailForm.submit();
    } else {
      // Check if there is a need to show the "expired vaccination" modal
      this.hasExpiredVaccination = this._hasExpiredVaccinationForReservation();
      if (this.hasExpiredVaccination && !this.modalOpened) {
        this.openExpiredVaccinationModal();
      } else {
        // Direct save on reservation
        this.onSave();
      }
    }
  }

  openExpiredVaccinationModal(): void {
    this.modalOpened = true;
    const dialogRef = this.dialog.open(ReservationVaccineDialogComponent, {
      data: {
        petList: this.petListWithExpiredVaccinations,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      this._analytics.logButtonTap(
        'reservationRequest_closeExpiredVaccinationScreen'
      );
      this.modalOpened = false;

      // Proceed on reservation save
      this.onSave();
    });
  }

  onSave(): void {
    this.navigateToNext.emit();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Payment <-
  // ----------

  submittedInSuccess(event) {
    //
    this.loadingIndicatorService.show();

    //
    const formGroup: FormGroup = event as FormGroup;
    //
    const newCardInfo: PaymentCreditCard = {
      cardToken: formGroup.controls.cardToken.value,
      cardholderName: formGroup.controls.cardholderName.value,
      cardNickname: formGroup.controls.cardNickname.value,
      cardType: formGroup.controls.cardType.value,
      isPrimary: !this.paymentService.hasPrimaryPaymentCard(),
    };

    this.paymentService
      .addPaymentCard(newCardInfo, this.selectedLocation.id)
      .pipe(
        first(),
        finalize(() => this.loadingIndicatorService.hide())
      )
      .subscribe((paymentCreditCard) => {
        // Show successful message
        this._showSuccessDialog(paymentCreditCard);
      });
  }

  submittedInFailure(event) {
    //
    const message =
      event.toLowerCase().charAt(0).toUpperCase() +
      event.toLowerCase().slice(1);
    this.dialogService.showAlert({ title: 'Error', content: message });
  }

  toggleCheckbox() {
    this.checkboxConsent = !this.checkboxConsent;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _resetData(): void {
    //
    this.selectedLocation = this.reservationRequest.selectedLocation;
    this.isCcpEnabledInLocation = this.myProfileService.isCcpEnabled(
      this.selectedLocation
    );

    //
    this.petList = this.reservationRequest.selectedPets;
    // Build the "pet name" list
    this.petNames = {};
    this.petList.forEach((pet) => {
      //
      this.petNames[pet.id] = pet.nickName
        ? `${pet.firstName} ${pet.lastName} (${pet.nickName})`
        : `${pet.firstName} ${pet.lastName}`;
    });

    //
    this.coreServices = lodash.groupBy(
      this.reservationRequest.coreServices,
      'serviceCategoryName'
    );

    //
    this.dateRange = undefined;
    this.startDate = undefined;
    this.endDate = undefined;
    this.dateRange = this.reservationRequest.selectedDateRange;
    if (this.dateRange) {
      this.startDate = this.dateRange.start.utc().format('dddd, MMMM DD, YYYY');
      if (this.dateRange.end) {
        this.endDate = this.dateRange.end.utc().format('dddd, MMMM DD, YYYY');
      }
    }

    //
    this.addonServices = lodash.cloneDeep(
      this.reservationRequest.addonServices
    );

    //
    this.reservationNotes = this.reservationRequest.reservationNotes;

    // Calc the "total price" of all service
    this.total = this.reservationRequest.calcTotalPriceOfAllServices();

    // Check expired vaccinations
    this.hasExpiredVaccination = false;
  }

  private _hasExpiredVaccinationForReservation(): any {
    //
    this.petListWithExpiredVaccinations = [];
    let hasExpiredRecord = false;
    for (const pet of this.petList) {
      const newPetWithExpiredVaccinations = lodash.cloneDeep(pet);
      const expiredVaccinations = [];
      //
      for (const vaccine of newPetWithExpiredVaccinations.vaccines) {
        const reservationEndDate = this.dateRange.end
          ? moment(this.dateRange.end._d)
          : moment(this.dateRange.start._d);

        //
        if (reservationEndDate.diff(moment(vaccine.expiresOn)) > 0) {
          // expired vaccine
          expiredVaccinations.push(lodash.cloneDeep(vaccine));
        }
      }

      // Check if this pet has expired vaccinations
      if (expiredVaccinations.length !== 0) {
        hasExpiredRecord = true;
        newPetWithExpiredVaccinations.vaccines =
          lodash.cloneDeep(expiredVaccinations);
        this.petListWithExpiredVaccinations.push(newPetWithExpiredVaccinations);
      }
    }

    return hasExpiredRecord;
  }

  private _showSuccessDialog(card: PaymentCreditCard) {
    //
    const title = 'Credit Card Added!';
    const content = `The credit card (${card.cardAccount}) has been added to your account successfully.`;

    this.dialogService.showAlert({
      title,
      content,
      okCallback: () => {
        // Payment flag
        this.hasPaymentMethod = true;

        // Re-start the reservation process.
        this.hasExpiredVaccination =
          this._hasExpiredVaccinationForReservation();
        if (this.hasExpiredVaccination && !this.modalOpened) {
          this.openExpiredVaccinationModal();
        } else {
          // Direct save on reservation
          this.onSave();
        }
      },
    });
  }

  // ----------
  // #endregion

  /////////////////////////////
}
